import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../api/api';
import { Dipendente } from '../types/types';

interface DipendenteMomentaneo {
  codicePersonale: string;
  neg: string;
  nome: string;
  oreSettimanali: number;
  livello?: number;
  dataAssunzione?: string;
  dataFineContratto?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfermed: () => void;
  negozio: string;
  /** Nome italiano del mese selezionato, es. "Aprile". Inviato al backend per generare la baseline. */
  mese?: string;
  /** Anno numerico, es. 2026. Inviato al backend per generare la baseline. */
  anno?: number;
}

type Step = 1 | 2 | 3;

const ConfermaDipendenteDialog: React.FC<Props> = ({ open, onClose, onConfermed, negozio, mese, anno }) => {
  const [momentanei, setMomentanei] = useState<DipendenteMomentaneo[]>([]);
  const [ufficiali, setUfficiali] = useState<Dipendente[]>([]);
  const [selectedMomentaneo, setSelectedMomentaneo] = useState<DipendenteMomentaneo | null>(null);
  const [selectedUfficiale, setSelectedUfficiale] = useState<Dipendente | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [conferming, setConferming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const loadData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const [momRes, uffRes] = await Promise.all([
        api.get<DipendenteMomentaneo[]>(`/dipendenti-momentanei/negozio/${negozio}`),
        api.get<Dipendente[]>(`/dipendenti/negozio/${negozio}`),
      ]);
      setMomentanei(momRes.data);
      setUfficiali(uffRes.data.filter(d => !d.codicePersonale.startsWith('TEST')));
    } catch (err: any) {
      setError('Errore nel caricamento dei dati.');
    } finally {
      setLoading(false);
    }
  }, [open, negozio]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset completo quando si chiude
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedMomentaneo(null);
      setSelectedUfficiale(null);
      setError(null);
      setShowAll(false);
    }
  }, [open]);

  const ufficialiFiltrati = showAll
    ? ufficiali
    : ufficiali.filter((d) => {
    if (!selectedMomentaneo) return false;
    const stessaAssunzione = d.dataAssunzione && selectedMomentaneo.dataAssunzione
      ? d.dataAssunzione === selectedMomentaneo.dataAssunzione
      : true;
    const stessaFine = d.dataFineContratto && selectedMomentaneo.dataFineContratto
      ? d.dataFineContratto === selectedMomentaneo.dataFineContratto
      : true;
    return stessaAssunzione && stessaFine && d.dataFineContratto !== '2099-12-31';
  });

  const handleClose = () => {
    onClose();
  };

  const handleConferma = async () => {
    if (!selectedMomentaneo || !selectedUfficiale) return;
    setConferming(true);
    setError(null);
    try {
      await api.post('/dipendenti-momentanei/conferma', {
        codiceMomentaneo: selectedMomentaneo.codicePersonale,
        codiceDipendenteUfficiale: selectedUfficiale.codicePersonale,
        mese: mese ?? null,
        anno: anno ?? null,
      });
      setStep(1);
      setSelectedMomentaneo(null);
      setSelectedUfficiale(null);
      onConfermed();
    } catch (err: any) {
      const msg = err.response?.data || err.message || 'Errore durante la conferma.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setStep(2);
    } finally {
      setConferming(false);
    }
  };

  const stepTitles: Record<Step, string> = {
    1: 'Conferma Dipendente — Passo 1: Dipendente Momentaneo',
    2: 'Conferma Dipendente — Passo 2: Dipendente Ufficiale',
    3: 'Conferma Dipendente — Passo 3: Conferma Abbinamento',
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Freccia indietro */}
        <IconButton
          size="small"
          onClick={() => {
            if (step === 1) {
              handleClose();
            } else if (step === 2) {
              setSelectedMomentaneo(null);
              setError(null);
              setStep(1);
            } else {
              setSelectedUfficiale(null);
              setError(null);
              setStep(2);
            }
          }}
          disabled={conferming}
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <span>{stepTitles[step]}</span>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* FASE 1: lista dipendenti momentanei */}
            {step === 1 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderRadius: '4px 4px 0 0' }}>
                  Dipendenti Momentanei ({momentanei.length})
                </Typography>
                <Divider />
                {momentanei.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Nessun dipendente momentaneo disponibile
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {momentanei.map((dm) => (
                      <ListItem key={dm.codicePersonale} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            setSelectedMomentaneo(dm);
                            setSelectedUfficiale(null);
                            setError(null);
                            setStep(2);
                          }}
                        >
                          <ListItemText
                            primary={dm.nome}
                            secondary={`${dm.codicePersonale} · ${dm.oreSettimanali}h · ass: ${dm.dataAssunzione ?? '—'}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* FASE 2: lista dipendenti ufficiali */}
            {step === 2 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Dipendente momentaneo selezionato: <strong>{selectedMomentaneo?.nome}</strong> ({selectedMomentaneo?.codicePersonale})
                </Alert>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'action.hover', borderRadius: '4px 4px 0 0' }}>
                    <Typography variant="subtitle2">
                      Dipendenti Ufficiali ({ufficialiFiltrati.length})
                    </Typography>
                    <Button
                      size="small"
                      variant={showAll ? 'contained' : 'outlined'}
                      onClick={() => setShowAll(v => !v)}
                      sx={{ ml: 1, fontSize: '0.7rem', py: 0.2 }}
                    >
                      {showAll ? 'Solo compatibili' : 'Mostra tutti'}
                    </Button>
                  </Box>
                  <Divider />
                  {ufficialiFiltrati.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      Nessun dipendente ufficiale con data assunzione ({selectedMomentaneo?.dataAssunzione ?? '—'}) e data fine contratto ({selectedMomentaneo?.dataFineContratto ?? '—'}) corrispondenti
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                      {ufficialiFiltrati.map((d) => (
                        <ListItem key={d.codicePersonale} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              setSelectedUfficiale(d);
                              setError(null);
                              setStep(3);
                            }}
                          >
                            <ListItemText
                              primary={d.nome}
                              secondary={`${d.codicePersonale} · ${d.oreSettimanali}h · ass: ${d.dataAssunzione ?? '—'}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            )}

            {/* FASE 3: conferma abbinamento */}
            {step === 3 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Vuoi associare <strong>{selectedMomentaneo?.nome}</strong> ({selectedMomentaneo?.codicePersonale}) con il dipendente ufficiale <strong>{selectedUfficiale?.nome}</strong> ({selectedUfficiale?.codicePersonale})?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutti i turni creati con il codice momentaneo verranno aggiornati con il codice ufficiale e il dipendente momentaneo verrà eliminato.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {step === 3 && (
          <>
            <Button
              onClick={() => {
                setSelectedUfficiale(null);
                setError(null);
                setStep(2);
              }}
              disabled={conferming}
              variant="outlined"
            >
              No
            </Button>
            <Button
              onClick={handleConferma}
              variant="contained"
              color="primary"
              disabled={conferming}
              startIcon={conferming ? <CircularProgress size={16} /> : undefined}
            >
              Sì, Conferma
            </Button>
          </>
        )}
        {step !== 3 && (
          <Button onClick={handleClose} disabled={conferming}>Chiudi</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfermaDipendenteDialog;
